import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { ProposalJobDescriptionBreakDownMode } from '@anytag/graphql';
import { InfluencerAvatar, SpreadButton } from '@anytag/shared-molecules';
import { formatIntNumber, formatNumber, formatPercent } from '@anytag/shared-utils';
import { useGenerateInfluencerProfileLink, useTableInputCursor } from '@anytag/shared-hooks';
import { Anchor, CheckBox, TableCell, SocialIcon, AnchorWithStatus, CheckBoxProps } from '@anytag/shared-atoms';
import {
  EditableProposeInfluencersFieldKeys,
  ProposeInfluencersFormValues,
  usePackageContentInfluencersSearch
} from '@anytag/shared-organisms';
import { RowPackageData } from '../types';
import { useDeleteFromPackage } from './useDeleteFromPackage';

interface Props {
  currency: string;
  packageId?: number;
  listSelectedIds?: number[];
  selectButtonProps: CheckBoxProps;
  onRowSelect: (influencerId: number, socialAccountId?: number) => void;
  resetSelectedItem: () => void;
}

export const usePackageInfluencersTable = ({
  currency,
  packageId,
  onRowSelect,
  resetSelectedItem,
  selectButtonProps,
  listSelectedIds = []
}: Props) => {
  const { t } = useTranslation();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor();
  const { handleRemoveSmAccountFromPackage, deleting } = useDeleteFromPackage({ packageId, resetSelectedItem });
  const { jdMode, isEditMode, isViewMode } = usePackageContentInfluencersSearch();
  const { control, watch, setValue } = useFormContext<ProposeInfluencersFormValues>();

  const values = watch();

  const handleInputChange =
    (row: Row<RowPackageData>, key: EditableProposeInfluencersFieldKeys) =>
    ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (isEditMode) {
        // Make number always positive
        const inputValue = Math.abs(value ? Number(value) : 0);
        setValue(name as keyof ProposeInfluencersFormValues, value, { shouldDirty: true });

        switch (key) {
          case EditableProposeInfluencersFieldKeys.budget:
            setValue(
              `${row.index}-profit`,
              (inputValue - Number(values[`${row.index}-influencerCost`] ?? 0)).toString(),
              { shouldDirty: true }
            );
            break;
          case EditableProposeInfluencersFieldKeys.influencerCost:
            setValue(`${row.index}-profit`, (Number(values[`${row.index}-budget`] ?? 0) - inputValue).toString(), {
              shouldDirty: true
            });
            break;
          default:
            break;
        }
      }
    };
  const columns: ColumnDef<RowPackageData>[] = [
    {
      size: 40,
      accessorKey: 'select',
      meta: { sticky: true },
      header: () => <CheckBox className="w-4" help={t('Select all')} {...selectButtonProps} />,
      cell: ({ row }) => {
        const { influencer, socialAccount } = row.original;

        const isInfluencerSelected = listSelectedIds.includes(influencer.id);

        if (socialAccount) {
          const isAccountSelected = listSelectedIds.includes(socialAccount.id);

          return isEditMode ? (
            <CheckBox
              checked={isAccountSelected}
              onClick={() => onRowSelect(influencer.id, socialAccount.id)}
              help={t(isAccountSelected ? 'Unselect' : 'Select')}
            />
          ) : (
            <div className="box-border h-auto flex-none" />
          );
        }

        return isViewMode || jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN ? (
          <CheckBox
            checked={isInfluencerSelected}
            onClick={() => onRowSelect(influencer.id)}
            help={t(isInfluencerSelected ? 'Unselect' : 'Select')}
          />
        ) : (
          <div className="box-border h-auto flex-none" />
        );
      }
    },
    {
      accessorKey: 'influencer',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencers'),
      cell: ({ row }) => {
        const { influencer, socialAccount } = row.original;

        return socialAccount ? (
          <div className="account-cell">
            <SocialIcon size="24px" type={socialAccount.socialMedia} />
            <Anchor
              label={socialAccount.name}
              className="ml-4 text-sm"
              {...generateProfileLink(influencer.id.toString(), {
                filter: { id: socialAccount.id.toString(), sm: socialAccount.socialMedia }
              })}
            />
            {isViewMode ? (
              <SpreadButton
                options={[
                  {
                    icon: 'delete',
                    label: 'Remove',
                    onClick: () =>
                      handleRemoveSmAccountFromPackage({
                        id: socialAccount.id,
                        name: socialAccount.name,
                        socialMedia: socialAccount.socialMedia
                      })
                  }
                ]}
                className="ml-auto [&_img]:size-auto"
              />
            ) : null}
          </div>
        ) : (
          <div className="influencer-cell">
            <InfluencerAvatar size="32px" src={influencer.avatar} className="rounded-full" asChild />
            <AnchorWithStatus
              status={influencer.status}
              label={influencer.name}
              className="ml-2"
              {...generateProfileLink(influencer.id.toString())}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'followers',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Followers'),
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followersCount)} />
    },
    {
      accessorKey: 'engagementRateByViews',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Avg ER by views'),
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByViews, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagementRateByFollowers',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Avg ER by followers'),
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByFollowers, false)} unit="%" />
      )
    },
    {
      accessorKey: 'budget',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Budget'),
      cell: ({ row }) =>
        !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.budget, 0)} unit={t(currency)} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-budget` satisfies keyof ProposeInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                className="justify-end"
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableProposeInfluencersFieldKeys.budget)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) =>
        !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.influencerCost, 0)} unit={t(currency)} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-influencerCost` satisfies keyof ProposeInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                className="justify-end"
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableProposeInfluencersFieldKeys.influencerCost)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'profit',
      header: t<string>('HeaderColumn.Profit'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(Number(values[`${row.index}-profit`] ?? 0), 0)} unit={t(currency)} />
      )
    },
    {
      accessorKey: 'engagementProposed',
      header: t<string>('HeaderColumn.Engagement Proposed'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementProposed, 0)} />
    },
    {
      accessorKey: 'engagementPosted',
      header: t<string>('HeaderColumn.Engagement Posted'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementPosted, 0)} />
    },
    {
      accessorKey: 'addedDate',
      header: t<string>('HeaderColumn.List Added Date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.stats.addedDate} />
    }
  ];

  return { columns, deleting };
};
